// Align and distribute (DRAW_PLAN §1 "Arrange"): pure functions that return how far to move each object.
// Reference: the selection's bounds, a key object (which does not move), or the artboard.
import type { Rect } from '../model/types';

export type AlignOp = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export type DistributeOp = AlignOp | 'hspace' | 'vspace';

const at = (r: Rect, op: AlignOp) =>
  op === 'left' ? r.x : op === 'hcenter' ? r.x + r.w / 2 : op === 'right' ? r.x + r.w : op === 'top' ? r.y : op === 'vcenter' ? r.y + r.h / 2 : r.y + r.h;
const horizontal = (op: AlignOp) => op === 'left' || op === 'hcenter' || op === 'right';

export function union(rs: Rect[]): Rect {
  const x = Math.min(...rs.map((r) => r.x)),
    y = Math.min(...rs.map((r) => r.y));
  return { x, y, w: Math.max(...rs.map((r) => r.x + r.w)) - x, h: Math.max(...rs.map((r) => r.y + r.h)) - y };
}

/** Moves (dx, dy) that align every box's `op` edge / centre with the reference's. */
export function align(boxes: Map<string, Rect>, op: AlignOp, ref: Rect): Map<string, [number, number]> {
  const target = at(ref, op);
  const out = new Map<string, [number, number]>();
  for (const [id, r] of boxes) {
    const d = target - at(r, op);
    out.set(id, horizontal(op) ? [d, 0] : [0, d]);
  }
  return out;
}

/**
 * Distribute: edges / centres evenly between the outermost two (they stay), or equal gaps (hspace / vspace).
 * With `spacing` (pt) the gaps are exactly that, measured from the key object (or the first box) outwards.
 * `keyId` never moves.
 */
export function distribute(boxes: Map<string, Rect>, op: DistributeOp, spacing?: number, keyId?: string): Map<string, [number, number]> {
  const out = new Map<string, [number, number]>();
  const entries = [...boxes.entries()];
  if (entries.length < 2) return out;
  const horiz = op === 'hspace' || (op !== 'vspace' && horizontal(op as AlignOp));
  const lo = (r: Rect) => (horiz ? r.x : r.y),
    size = (r: Rect) => (horiz ? r.w : r.h);
  const move = (d: number): [number, number] => (horiz ? [d, 0] : [0, d]);
  if (op === 'hspace' || op === 'vspace') {
    entries.sort((a, b) => lo(a[1]) - lo(b[1]));
    let gap: number;
    let pos: number;
    if (spacing !== undefined) {
      gap = spacing;
      const start = Math.max(0, keyId ? entries.findIndex((e) => e[0] === keyId) : 0);
      pos = lo(entries[start]![1]);
      // Outwards from the anchor box in both directions.
      let p = pos + size(entries[start]![1]);
      for (let i = start + 1; i < entries.length; i++) {
        p += gap;
        out.set(entries[i]![0], move(p - lo(entries[i]![1])));
        p += size(entries[i]![1]);
      }
      p = pos;
      for (let i = start - 1; i >= 0; i--) {
        p -= gap + size(entries[i]![1]);
        out.set(entries[i]![0], move(p - lo(entries[i]![1])));
      }
      out.set(entries[start]![0], [0, 0]);
      return out;
    }
    const first = entries[0]![1],
      last = entries[entries.length - 1]![1];
    const span = lo(last) + size(last) - lo(first);
    const total = entries.reduce((s, e) => s + size(e[1]), 0);
    gap = (span - total) / (entries.length - 1);
    pos = lo(first);
    for (const [id, r] of entries) {
      out.set(id, move(pos - lo(r)));
      pos += size(r) + gap;
    }
    return out;
  }
  const key = (r: Rect) => at(r, op as AlignOp);
  entries.sort((a, b) => key(a[1]) - key(b[1]));
  const k0 = key(entries[0]![1]),
    k1 = key(entries[entries.length - 1]![1]);
  entries.forEach(([id, r], i) => {
    const target = k0 + ((k1 - k0) * i) / (entries.length - 1);
    out.set(id, move(target - key(r)));
  });
  if (keyId && out.has(keyId)) {
    // The key object stays: shift everything by its correction.
    const [kx, ky] = out.get(keyId)!;
    for (const [id, [x, y]] of out) out.set(id, [x - kx, y - ky]);
  }
  return out;
}
